// Validation utility functions for the createcase component

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  fieldErrors: Record<string, string>;
}

export interface NextOfKin {
  name: string;
  email: string;
  phone_number: string;
}

export interface PartyData {
  partyType: string;
  first_name: string;
  last_name: string;
  other_name: string;
  email: string;
  address: string;
  phone_number: string;
  state: string;
  lga: string;
  children_names: string[];
  next_of_kin: NextOfKin[];
}

export interface CounselData {
  counsel_detail_first_name: string;
  counsel_detail_last_name: string;
  counsel_detail_firm_name: string;
  counsel_detail_email: string;
  counsel_detail_address: string[];
  counsel_detail_phone_number: string;
  counsel_detail_whatsapp_number: string;
}

export interface CaseDocument {
  title: string;
  document_type: string;
  file: File | null;
}

// Email validation with comprehensive regex
export const validateOptionalEmail = (email: string | undefined): boolean => {
  if (!email || email.trim() === "") return true; // Optional

  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email.trim());
};

// Phone number validation for Nigerian numbers
export const validatePhoneNumber = (phone: string): boolean => {
  if (!phone || typeof phone !== "string") return false;

  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, "");

  // Nigerian phone number patterns
  const patterns = [
    /^0[789][01]\d{8}$/, // 080, 081, 070, 071, 090, 091
    /^\+234[789][01]\d{8}$/, // +234 format
    /^234[789][01]\d{8}$/, // 234 format without +
  ];

  return patterns.some((pattern) => pattern.test(cleanPhone));
};

// Name validation
export const validateName = (
  name: string,
  fieldName: string = "Name",
): string | null => {
  if (!name || typeof name !== "string") {
    return `${fieldName} is required`;
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    return `${fieldName} must be at least 2 characters long`;
  }

  if (trimmedName.length > 100) {
    return `${fieldName} must not exceed 100 characters`;
  }

  return null;
};

// Address validation
export const validateAddress = (address: string): string | null => {
  if (!address || typeof address !== "string") {
    return "Address is required";
  }

  const trimmedAddress = address.trim();

  if (trimmedAddress.length < 3) {
    return "Address must be at least 3 characters long";
  }

  if (trimmedAddress.length > 500) {
    return "Address must not exceed 500 characters";
  }

  return null;
};

// Party type validation
export const validatePartyType = (partyType: string): string | null => {
  const validPartyTypes = [
    "CLAIMANT",
    "APPLICANT",
    "PETITIONER",
    // "PETITIONAL",
    "DEFENDANT",
    "RESPONDENT",
    "PLAINTIFF",
    "PROBATION OFFICER",
    "CHILD DEVELOPMENT OFFICER",
    "STATE",
    "COMMISSIONER OF POLICE",
    // "WELFARE OFFICER",
    // "SOCIAL WELFARE",
  ];

  if (!partyType || typeof partyType !== "string") {
    return "Party type is required";
  }

  if (!validPartyTypes.includes(partyType)) {
    return "Please select a valid party type";
  }

  return null;
};

// File validation
export const validateFile = (
  file: File | null,
  maxSizeMB: number = 25,
): string | null => {
  if (!file) {
    return "File is required";
  }

  // Check file type
  if (file.type !== "application/pdf") {
    return "Only PDF files are allowed";
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return `File size must not exceed ${maxSizeMB}MB`;
  }

  // Check file name
  if (!file.name || file.name.trim().length === 0) {
    return "File must have a valid name";
  }

  return null;
};

// Document title validation
export const validateDocumentTitle = (title: string): string | null => {
  if (!title || typeof title !== "string") {
    return "Document title is required";
  }

  // const trimmedTitle = title.trim();

  if (title.length < 3) {
    return "Document title must be at least 3 characters long";
  }

  if (title.length > 100) {
    return "Document title must not exceed 100 characters";
  }

  return null;
};

// Validate individual party
export const validateParty = (
  party: PartyData,
  partyIndex: number,
  partyType: "plaintiff" | "defendant",
  caseCategory?: string,
): ValidationResult => {
  const errors: string[] = [];
  const fieldErrors: Record<string, string> = {};

  // Validate party type
  const partyTypeError = validatePartyType(party.partyType);
  if (partyTypeError) {
    errors.push(partyTypeError);
    fieldErrors.partyType = partyTypeError;
  }

  // Validate first name
  const firstNameError = validateName(party.first_name, "First name");
  if (firstNameError) {
    errors.push(firstNameError);
    fieldErrors.first_name = firstNameError;
  }

  // Validate last name
  const lastNameError = validateName(party.last_name, "Last name");
  if (lastNameError) {
    errors.push(lastNameError);
    fieldErrors.last_name = lastNameError;
  }

  // Validate other name (optional)
  if (party.other_name && party.other_name.trim()) {
    const otherNameError = validateName(party.other_name, "Other name");
    if (otherNameError) {
      errors.push(otherNameError);
      fieldErrors.other_name = otherNameError;
    }
  }

  // Validate email (Optional)
  if (!validateOptionalEmail(party.email)) {
    const emailError = "Please enter a valid email address";
    fieldErrors.email = emailError;
  }

  // Validate children names (Compulsory only for social welfare)
  if (caseCategory === "social welfare") {
    if (!party.children_names || party.children_names.length === 0) {
      const childrenError =
        "At least one child name is required for social welfare cases";
      errors.push(childrenError);
      fieldErrors.children_names = childrenError;
    } else {
      party.children_names.forEach((child, idx) => {
        if (!child || child.trim().length < 2) {
          const childError = `Child ${idx + 1} name is required`;
          errors.push(childError);
          fieldErrors[`child_${idx}`] = childError;
        }
      });
    }
  }

  // Validate address
  const addressError = validateAddress(party.address);
  if (addressError) {
    errors.push(addressError);
    fieldErrors.address = addressError;
  }

  // Validate phone number
  if (party.phone_number && !validatePhoneNumber(party.phone_number)) {
    const phoneError =
      "Please enter a valid Nigerian phone number (e.g., 08012345678)";
    errors.push(phoneError);
    fieldErrors.phone_number = phoneError;
  }

  return {
    isValid: errors.length === 0,
    errors,
    fieldErrors,
  };
};

// Validate counsel details
export const validateCounsel = (counsel: CounselData): ValidationResult => {
  const errors: string[] = [];
  const fieldErrors: Record<string, string> = {};

  // Validate first name
  const firstNameError = validateName(
    counsel.counsel_detail_first_name,
    "First name",
  );
  if (firstNameError) {
    errors.push(firstNameError);
    fieldErrors.counsel_detail_first_name = firstNameError;
  }

  // Validate last name
  const lastNameError = validateName(
    counsel.counsel_detail_last_name,
    "Last name",
  );
  if (lastNameError) {
    errors.push(lastNameError);
    fieldErrors.counsel_detail_last_name = lastNameError;
  }

  // Validate firm name
  const firmNameError = validateName(
    counsel.counsel_detail_firm_name,
    "Firm name",
  );
  if (firmNameError) {
    errors.push(firmNameError);
    fieldErrors.counsel_detail_firm_name = firmNameError;
  }

  // Validate email (Optional)
  if (!validateOptionalEmail(counsel.counsel_detail_email)) {
    const emailError = "Please enter a valid email address";
    fieldErrors.counsel_detail_email = emailError;
  }

  // Validate addresses
  if (
    !counsel.counsel_detail_address ||
    counsel.counsel_detail_address.length === 0
  ) {
    errors.push("At least one address for service is required");
    fieldErrors.counsel_detail_address = "At least one address is required";
  } else {
    counsel.counsel_detail_address.forEach((address, index) => {
      const addressError = validateAddress(address);
      if (addressError) {
        errors.push(`Address ${index + 1}: ${addressError}`);
        fieldErrors[`counsel_detail_address_${index}`] = addressError;
      }
    });
  }

  // Validate phone number
  if (
    counsel.counsel_detail_phone_number &&
    !validatePhoneNumber(counsel.counsel_detail_phone_number)
  ) {
    const phoneError = "Please enter a valid Nigerian phone number";
    errors.push(phoneError);
    fieldErrors.counsel_detail_phone_number = phoneError;
  }

  // Validate WhatsApp number
  if (
    counsel.counsel_detail_whatsapp_number &&
    !validatePhoneNumber(counsel.counsel_detail_whatsapp_number)
  ) {
    const whatsappError = "Please enter a valid Nigerian WhatsApp number";
    errors.push(whatsappError);
    fieldErrors.counsel_detail_whatsapp_number = whatsappError;
  }

  return {
    isValid: errors.length === 0,
    errors,
    fieldErrors,
  };
};

// Validate case documents
export const validateCaseDocuments = (
  documents: CaseDocument[],
): ValidationResult => {
  const errors: string[] = [];
  const fieldErrors: Record<string, string> = {};

  if (!documents || documents.length === 0) {
    errors.push("At least one document is required");
    return { isValid: false, errors, fieldErrors };
  }

  documents.forEach((doc, index) => {
    const docPrefix = `document_${index}`;

    // Validate title
    const titleError = validateDocumentTitle(doc.title);
    if (titleError) {
      errors.push(`Document ${index + 1}: ${titleError}`);
      fieldErrors[`${docPrefix}_title`] = titleError;
    }

    // Validate file
    const fileError = validateFile(doc.file);
    if (fileError) {
      errors.push(`Document ${index + 1}: ${fileError}`);
      fieldErrors[`${docPrefix}_file`] = fileError;
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    fieldErrors,
  };
};

// Validate entire form
export const validateCreateCaseForm = (
  plaintiffField: PartyData[],
  defendantField: PartyData[],
  counselData: CounselData,
  caseDocuments: CaseDocument[],
  caseCategory?: string,
): ValidationResult => {
  const errors: string[] = [];
  const fieldErrors: Record<string, string> = {};

  // Validate at least one plaintiff
  if (!plaintiffField || plaintiffField.length === 0) {
    errors.push("At least one plaintiff is required");
  } else {
    plaintiffField.forEach((plaintiff, index) => {
      const plaintiffValidation = validateParty(
        plaintiff,
        index,
        "plaintiff",
        caseCategory,
      );
      if (!plaintiffValidation.isValid) {
        errors.push(
          ...plaintiffValidation.errors.map(
            (error) => `Plaintiff ${index + 1}: ${error}`,
          ),
        );
        Object.keys(plaintiffValidation.fieldErrors).forEach((field) => {
          fieldErrors[`plaintiff_${index}_${field}`] =
            plaintiffValidation.fieldErrors[field];
        });
      }
    });
  }

  // Validate at least one defendant
  if (!defendantField || defendantField.length === 0) {
    errors.push("At least one defendant is required");
  } else {
    defendantField.forEach((defendant, index) => {
      const defendantValidation = validateParty(
        defendant,
        index,
        "defendant",
        caseCategory,
      );
      if (!defendantValidation.isValid) {
        errors.push(
          ...defendantValidation.errors.map(
            (error) => `Defendant ${index + 1}: ${error}`,
          ),
        );
        Object.keys(defendantValidation.fieldErrors).forEach((field) => {
          fieldErrors[`defendant_${index}_${field}`] =
            defendantValidation.fieldErrors[field];
        });
      }
    });
  }

  // Validate counsel
  const counselValidation = validateCounsel(counselData);
  if (!counselValidation.isValid) {
    errors.push(...counselValidation.errors);
    Object.assign(fieldErrors, counselValidation.fieldErrors);
  }

  // Validate documents
  const documentsValidation = validateCaseDocuments(caseDocuments);
  if (!documentsValidation.isValid) {
    errors.push(...documentsValidation.errors);
    Object.assign(fieldErrors, documentsValidation.fieldErrors);
  }

  return {
    isValid: errors.length === 0,
    errors,
    fieldErrors,
  };
};

// Format phone number for display
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  const cleanPhone = phone.replace(/\D/g, "");

  if (cleanPhone.startsWith("234")) {
    return `+${cleanPhone}`;
  } else if (cleanPhone.startsWith("0")) {
    return `+234${cleanPhone.substring(1)}`;
  }

  return cleanPhone;
};

// Sanitize input to prevent XSS
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== "string") return "";

  return input
    .replace(/[<>]/g, "") // Remove potential HTML tags
    .replace(/javascript:/gi, "") // Remove javascript: protocol
    .replace(/on\w+=/gi, ""); // Remove event handlers
};
